import * as Sentry from '@sentry/browser';
import moment from 'moment';

import type {
  DateInterpreterConfig,
  FilterConfig,
  InterpreterConfig
} from '@/components/ui/table/filters/filters.type';
import type { FilterCondition } from '@/utils/filtering/filtering.type';
import { getByPath, numberSatisfiesCondition } from '@/utils/widgets';

const NON_FILTERABLE_PATH_CHARS = /[`'"\\;$|@?*()[\]<>=,]|\s/;

export const isFilterablePath = (path: unknown): path is string =>
  typeof path === 'string' &&
  path.length > 0 &&
  !NON_FILTERABLE_PATH_CHARS.test(path);

const getInterpreterConfigType = (
  interpreterConfig: Maybe<InterpreterConfig>
) => (interpreterConfig?.type || '').toLowerCase();

const getTextDataToMatch = (
  path: string,
  dataRow: unknown,
  interpreterConfig?: InterpreterConfig
): string => {
  let dataToMatch = getByPath(dataRow, path) || '';
  if (typeof dataToMatch === 'object') {
    dataToMatch = JSON.stringify(dataToMatch);
  }
  if (getInterpreterConfigType(interpreterConfig) === 'date') {
    // cast to the required format
    // to apply correct comparison with what the user is seeing
    // when inputting the filter value
    const { inputFormat, format } = interpreterConfig as DateInterpreterConfig;
    dataToMatch = moment(dataToMatch, inputFormat).format(format);
  }
  return dataToMatch.toString();
};

// simple search of string occurrence, or exact match when `exact` is true
// (used by 'select' filters, so that eg. selecting "Assigned" doesn't also
// return "Unassigned" as a substring match)
const applySearchFilter = (
  path: string,
  interpreterConfig: Maybe<InterpreterConfig>,
  filterValue: string,
  data: unknown[],
  exact = false
) => {
  // build the list of values to search for
  const filterConditions = filterValue
    .split(',')
    .map(filterCondition => filterCondition.trim().toUpperCase());

  return data.filter(dataRow => {
    const dataToMatch = getTextDataToMatch(
      path,
      dataRow,
      interpreterConfig
    ).toUpperCase();
    for (let idx = 0; idx < filterConditions.length; idx += 1) {
      const match = filterConditions[idx];
      if (match === '') continue;
      if (exact ? dataToMatch === match : dataToMatch.includes(match)) {
        return true;
      }
    }
    return false;
  });
};

// regex patttern match
const applyRegexFilter = (
  path: string,
  interpreterConfig: Maybe<InterpreterConfig<'regex'>>,
  filterValue: string,
  data: unknown[]
): unknown[] => {
  // try to build the regular expression object
  try {
    const regExpr = new RegExp(filterValue, interpreterConfig?.flags);

    return data.filter(dataRow => {
      const dataToMatch = getTextDataToMatch(path, dataRow);
      return regExpr.test(dataToMatch);
    });
  } catch (err) {
    // if the regex is invalid just send the original data
    console.error(`Invalid regular expression: ${filterValue}`, err);
    Sentry.captureException(err, {
      level: 'error',
      extra: {
        section: 'applyRegexFilter',
        path,
        filterValue,
        interpreterConfig: JSON.stringify(interpreterConfig, null, 2)
      }
    });
    return data;
  }
};

const getConditionNumberValue = numberString => {
  if (numberString === '') return;
  const numberValue = Number(numberString);
  if (Number.isNaN(numberValue)) return;
  return numberValue;
};
// number match
const applyNumberFilter = (
  path: string,
  filterValue: string,
  data: unknown[]
): unknown[] => {
  // build the list of number conditions to check for
  const filterConditions = filterValue.split(',').map(filterCondition => {
    if (!filterCondition) return null;
    if (filterCondition.includes('<>')) {
      // difference condition eg <>3
      return {
        notEqual: getConditionNumberValue(filterCondition.split('<>')[1])
      };
    }
    if (filterCondition.includes('<<')) {
      // range condition eg 10<<=15
      const bits = filterCondition.split('<<');
      const condition: FilterCondition = {};
      // lower bound
      if (bits[0].includes('=')) {
        // inclusive lower bound
        condition.minimum = getConditionNumberValue(bits[0].split('=')[0]);
      } else {
        // exclusive lower bound
        condition.exclusiveMinimum = getConditionNumberValue(bits[0]);
      }
      // upper bound
      if (bits[1].includes('=')) {
        // inclusive upper bound
        condition.maximum = getConditionNumberValue(bits[1].split('=')[1]);
      } else {
        // exclusive upper bound
        condition.exclusiveMaximum = getConditionNumberValue(bits[1]);
      }
      // return the condition
      return condition;
    }
    if (filterCondition.includes('<')) {
      // lower than condition eg <=15
      const bits = filterCondition.split('<');
      const condition: FilterCondition = {};
      // upper bound
      if (bits[1].includes('=')) {
        // inclusive upper bound
        condition.maximum = getConditionNumberValue(bits[1].split('=')[1]);
      } else {
        // exclusive upper bound
        condition.exclusiveMaximum = getConditionNumberValue(bits[1]);
      }
      // return the condition
      return condition;
    }
    if (filterCondition.includes('>')) {
      // greater than condition eg >=10
      const bits = filterCondition.split('>');
      const condition: FilterCondition = {};
      // lower bound
      if (bits[1].includes('=')) {
        // inclusive lower bound
        condition.minimum = getConditionNumberValue(bits[1].split('=')[1]);
      } else {
        // exclusive lower bound
        condition.exclusiveMinimum = getConditionNumberValue(bits[1]);
      }
      // return the condition
      return condition;
    }
    // by default it is interpreted as a number equality
    return {
      equal: getConditionNumberValue(filterCondition)
    };
  });

  return data.filter(dataRow => {
    const dataToMatch = Number(getByPath(dataRow, path));
    if (Number.isNaN(dataToMatch)) {
      return false;
    }
    for (let idx = 0; idx < filterConditions.length; idx += 1) {
      const condition = filterConditions[idx];
      if (condition && numberSatisfiesCondition(dataToMatch, condition)) {
        return true;
      }
    }
    return false;
  });
};

// check for a row matching a text input at a given path
// `exact` switches the default substring search to an exact match (used by 'select' filters)
const applyTextInputFilter = (
  path: string,
  interpreterConfig: Maybe<InterpreterConfig>,
  filterValue: FilterConfig<'select' | 'text'>['value'],
  data: unknown[],
  exact = false
): unknown[] => {
  // filter value needs to be a real string
  if (typeof filterValue !== 'string') return data;

  switch (getInterpreterConfigType(interpreterConfig)) {
    case 'regex':
      return applyRegexFilter(
        path,
        interpreterConfig as Maybe<InterpreterConfig<'regex'>>,
        filterValue,
        data
      );
    case 'number':
      return applyNumberFilter(path, filterValue, data);
    default:
      return applySearchFilter(
        path,
        interpreterConfig as Maybe<InterpreterConfig<'date' | 'number'>>,
        filterValue,
        data,
        exact
      );
  }
};

// check for a date being in a range
const applyDateRangeFilter = (
  path: string,
  format: moment.MomentFormatSpecification | undefined,
  inputFormat: moment.MomentFormatSpecification | undefined,
  filterValue: FilterConfig<'dateRange'>['value'],
  data: unknown[]
): unknown[] => {
  // filter value needs to be a real js object
  if (typeof filterValue !== 'object' || Array.isArray(filterValue)) {
    return data;
  }

  const { startDate, endDate } = filterValue;
  const startDateObj = startDate
    ? moment.utc(startDate, format, true).local()
    : undefined;
  const endDateObj = endDate
    ? moment.utc(endDate, format, true).local()
    : undefined;

  return data.filter(dataRow => {
    // get the value at the specified path for this row
    const dateToMatchStr = getByPath(dataRow, path);
    if (dateToMatchStr === undefined || typeof dateToMatchStr !== 'string')
      return false;

    // build the date and check it is a valid one
    const dateToMatch = moment.utc(dateToMatchStr, inputFormat, true).local();
    if (!dateToMatch.isValid()) return false;

    // compare to start and an end dates
    if (startDateObj && dateToMatch.diff(startDateObj) < 0) return false;
    if (endDateObj && dateToMatch.diff(endDateObj) > 0) return false;

    return true;
  });
};

// client side filter

export const filterData = (
  dataArray: unknown[],
  filterConfig: FilterConfig,
  filterValue: FilterConfig['value']
): unknown[] => {
  // console logging of the filter execution, very useful for debugging but commented out in prod not to slow UX down
  // console.info(`filtering ${JSON.stringify(filterConfig)} ${filterValue}`);

  const { path, type } = filterConfig;
  // switch filter interface type, default to 'text'
  switch (type) {
    case 'dateRange':
      // date range picker interface
      return applyDateRangeFilter(
        path,
        filterConfig.format,
        filterConfig.inputFormat,
        filterValue as (typeof filterConfig)['value'],
        dataArray
      );
    case 'select':
      // enum select interface: same as text input, but exact match
      return applyTextInputFilter(
        path,
        filterConfig.interpreter,
        filterValue as (typeof filterConfig)['value'],
        dataArray,
        true
      );
    default:
      // text input interface
      return applyTextInputFilter(
        path,
        filterConfig.interpreter,
        filterValue as (typeof filterConfig)['value'],
        dataArray
      );
  }
};
